/**
 * One request per thing, not one per mount (CT-5579).
 *
 * ## What this fixes
 *
 * Opening the widget builder and moving between its two tabs re-listed the
 * composite entities every time. Measured on dev 2026-10-07: **four calls to
 * `/datamodel/composite-entity/list` for a single visit to the Data step**, and
 * sixteen after a handful of clicks between tabs and between the Entity and
 * Existing-dataset sources.
 *
 * Three separate causes, which is why the fix is here and not in a component:
 *
 * 1. **Two independent consumers of the same list.** `EntityPicker` calls
 *    `useEntities()` for the short list and `useEntityPicker` calls it again
 *    for the modal. Neither knows about the other, and they fire together.
 * 2. **Refetch on remount.** The pickers live inside the Data step, which
 *    unmounts when the author switches tab or switches source, so every return
 *    starts the request again. `useWidgetTypes` does not have this problem for
 *    one reason only: it is mounted at `WidgetPlayground`, above the tabs.
 * 3. **`React.StrictMode`** double-invokes effects in development, doubling
 *    whatever the first two produce. That half is dev-only and was never the
 *    real cost — but it is the half that makes the number look alarming.
 *
 * ## Why a cache in the services and not a React context
 *
 * A provider at `WidgetPlayground` would fix (2) alone. It would not stop two
 * sibling hooks from each firing their own request unless both were rewired
 * through it, and it would start over on every navigation — opening a widget
 * to edit and coming back re-lists everything.
 *
 * Caching the **promise** fixes all three at once and asks nothing of the
 * components: callers that arrive while a request is in flight join it instead
 * of starting another, and callers that arrive after it resolved get the
 * answer without a request. The hooks keep their own loading and error states;
 * what changes is how often the network is actually touched.
 *
 * ## What it deliberately does not do
 *
 * It is not a client-side store, and it does not try to be `react-query`. There
 * is no background revalidation, no subscriptions, no normalisation: entries
 * expire, and anything that has to be fresh right now asks for it through
 * `invalidate`. Keeping it this small is what makes it safe to put under
 * catalogs that several hooks read.
 */

/**
 * How long a resolved answer stays good.
 *
 * These are catalogs — composite entities, their fields, the presentation
 * types — edited in Data Studio by somebody else, on a timescale of days.
 * Five minutes is long enough that a builder session touches each one once and
 * short enough that a model changed mid-session is picked up without anybody
 * reloading the page. A caller that cannot wait calls `invalidate` instead.
 */
export const DEFAULT_TTL_MS = 5 * 60 * 1000;

/** `key → { promise, expiresAt }`. Module-level on purpose: see the note above. */
const entries = new Map();

/**
 * Run `loader` once per key, and share the result until it expires.
 *
 * @param {string} key Identifies the request, parameters included — two
 *   searches are two requests.
 * @param {() => Promise<any>} loader Called only on a miss.
 * @param {{ ttlMs?: number }} [options]
 * @returns {Promise<any>} The shared promise.
 */
export function cachedRequest(key, loader, { ttlMs = DEFAULT_TTL_MS } = {}) {
  const hit = entries.get(key);
  if (hit && hit.expiresAt > Date.now()) return hit.promise;

  const promise = loader();
  entries.set(key, { promise, expiresAt: Date.now() + ttlMs });

  // A failure is not an answer worth keeping. Evicting it is what makes the
  // `refetch` behind every picker's "try again" actually try again, instead of
  // handing back the same rejection for the rest of the TTL.
  promise.catch(() => {
    if (entries.get(key)?.promise === promise) entries.delete(key);
  });

  return promise;
}

/**
 * Drop cached answers so the next call goes to the network.
 *
 * @param {string} [prefix] Clears the keys that start with it; everything when
 *   omitted. Prefixes are how one entity's fields are dropped without dropping
 *   another's — see the keys `dataModel.js` builds.
 */
export function invalidate(prefix) {
  if (prefix === undefined) {
    entries.clear();
    return;
  }
  [...entries.keys()]
    .filter((key) => key.startsWith(prefix))
    .forEach((key) => entries.delete(key));
}

/** Test seam: the cache outlives any one test, so each one starts clean. */
export const __resetRequestCacheForTests = () => entries.clear();
